import {Component, ElementRef, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren, ChangeDetectionStrategy} from '@angular/core';
import {Instance as PopperInstance, Modifier} from '@popperjs/core';
import * as _ from 'lodash-es';
import {DropdownMenuComponent} from '../../dropdown-menu';
import {DropdownMenuItem} from '../../dropdown-menu/dropdown-menu-item';
import {createPopperReferenceWideModifier} from '../../popper-utils/modifiers/popper-reference-wide';

interface DisplayMultiselectDropdownMenuItem extends DropdownMenuItem {
    isChecked?: boolean;
    isVariant?: boolean;
}

const ITEM_NOT_SELECTED_INDEX = -1;

const SEPARATOR_MENU_ITEM: DisplayMultiselectDropdownMenuItem = {
    label:       '',
    isSeparator: true
};

@Component({
    selector: 'ul[multiselect-dropdown-menu]',
    templateUrl: './multiselect-dropdown-menu.component.html',
    styleUrls: ['./multiselect-dropdown-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MultiselectDropdownMenuComponent extends DropdownMenuComponent implements OnInit {

    @Input()
    public override targetElement: ElementRef;

    @Input()
    public menuItemsConfig: DropdownMenuItem[];

    @Input()
    public noneMenuItem: DropdownMenuItem;

    @Input()
    public checkedVariantsConfig: any[];

    /**
     * Disables multi selection, allowed only one of these variants:
     * - Any or None: if present {@link noneMenuItem}, then only None, if not present, then only Any.
     * - one of options.
     */
    @Input()
    public isSingleSelectionMode: boolean = false;

    @Output()
    public readonly checkedVariantsChange$ = new EventEmitter<any[]>();

    @ViewChildren('item', {read: ElementRef})
    public override itemQueryList: QueryList<ElementRef>;

    public override preselectedMenuItemIndex: number = ITEM_NOT_SELECTED_INDEX;

    public override menuItems: DisplayMultiselectDropdownMenuItem[];

    protected override popper: PopperInstance;

    private selectAllMenuItem: DisplayMultiselectDropdownMenuItem;

    private internalNoneMenuItem: DisplayMultiselectDropdownMenuItem;

    private variantMenuItems: DisplayMultiselectDropdownMenuItem[];
    private checkedVariants: any[];

    public override ngOnInit(): void {
        this.createOverlay();
        this.initCheckedVariants(this.checkedVariantsConfig);
        this.initVariantMenuItems(
            this.menuItemsConfig,
            this.checkedVariants
        );

        this.internalNoneMenuItem = this.noneMenuItem
            && {
                ...this.noneMenuItem,
                isVariant: false
            };

        // Create menu items including select all, none, item and separator
        this.selectAllMenuItem = this.createSelectAllMenuItem();
        this.menuItems = [
            this.selectAllMenuItem,
            ...(this.internalNoneMenuItem ? [this.internalNoneMenuItem] : []),
            SEPARATOR_MENU_ITEM,
            ...this.variantMenuItems
        ];

        const isAllVariantsChecked = this.isAllVariantsChecked();
        const isNoneVariantsChecked = this.isNoneVariantsChecked();

        this.selectAllMenuItem.isChecked = isAllVariantsChecked;

        if (isAllVariantsChecked) {
            this.checkAllMenuItems(false);
        }

        if (this.internalNoneMenuItem) {
            this.internalNoneMenuItem.isChecked = isNoneVariantsChecked;
        }
    }

    public override onMenuItemClick(menuItem?: DisplayMultiselectDropdownMenuItem): void {
        if (menuItem && !menuItem.isSeparator) {
            this.selectPreselectedMenuItem(menuItem);
        }
    }

    public override selectPreselectedMenuItem(selectedMenuItem?: DisplayMultiselectDropdownMenuItem): void {
        const menuItem = selectedMenuItem || this.menuItems[this.preselectedMenuItemIndex];

        // 'Check all' menu item
        if (menuItem === this.selectAllMenuItem) {
            const doTickAllItems = !this.isSingleSelectionMode;

            this.checkAllMenuItems(doTickAllItems);

        // 'Check none' menu item
        } else if (menuItem === this.internalNoneMenuItem) {
            this.checkAllMenuItems(false);

        // Check/uncheck variant menu item
        } else if (menuItem.isVariant) {
            if (!this.isSingleSelectionMode) {
                menuItem.isChecked = !menuItem.isChecked;
            } else {
                this.checkAllMenuItems(false);
                menuItem.isChecked = true;
            }
        }

        this.updateCheckedVariants();
        this.selectAllMenuItem.isChecked = this.isAllVariantsChecked();

        if (this.internalNoneMenuItem) {
            this.internalNoneMenuItem.isChecked = this.isNoneVariantsChecked();
        }
    }

    protected override initPopper(): void {
        super.initPopper({
            placement: 'bottom-start'
        });
    }

    protected override getPopperModifiers(): Array<Modifier<any, any>> {
        return [
            ...super.getPopperModifiers(),
            createPopperReferenceWideModifier()
        ];
    }

    private initVariantMenuItems(
        menuItemsConfig: DropdownMenuItem[],
        checkedVariants: any[]
    ): void {
        this.variantMenuItems = menuItemsConfig.map(menuItem => ({
                label: menuItem.label,
                value: menuItem.value,
                isChecked: checkedVariants.indexOf(menuItem.value) > -1,
                isVariant: true
            })
        );
    }

    private initCheckedVariants(checkedVariantsConfig: any[]): void {
        this.checkedVariants = checkedVariantsConfig
            ? checkedVariantsConfig.slice()
            : [];
    }

    private checkAllMenuItems(isChecked: boolean): void {
        this.menuItems
            .forEach(menuItem => {
                if (menuItem.isVariant) {
                    menuItem.isChecked = isChecked;
                }
            });
    }

    private isNoneVariantsChecked(): boolean {
        const variantsCheckedQty: number = this.variantMenuItems
            .filter(item => item.isChecked).length;

        return (!variantsCheckedQty && !!this.noneMenuItem);
    }

    private isAllVariantsChecked(): boolean {
        const variantsCheckedQty: number = this.variantMenuItems
            .filter(item => item.isChecked).length;

        return (
            !this.isNoneVariantsChecked()
            && (!variantsCheckedQty || variantsCheckedQty === this.variantMenuItems.length)
        );
    }

    private updateCheckedVariants(): void {
        const newCheckedVariants = (
            this.isNoneVariantsChecked()
            || (this.isAllVariantsChecked() && !this.noneMenuItem)
        )
            ? []
            : this.variantMenuItems
                .filter((menuItem => menuItem.isVariant && menuItem.isChecked))
                .map(menuItem => menuItem.value);

        const leftDiff = _.difference(newCheckedVariants, this.checkedVariants);
        const rightDiff = _.difference(this.checkedVariants, newCheckedVariants);

        const isNewCheckedVariantsDifferent = leftDiff.length || rightDiff.length;

        if (isNewCheckedVariantsDifferent) {
            this.checkedVariants = newCheckedVariants;
            this.checkedVariantsChange$.emit(newCheckedVariants);
        }
    }

    private createSelectAllMenuItem(): DisplayMultiselectDropdownMenuItem {
        const label = this.isSingleSelectionMode
            ? 'components.filters.any'
            : 'components.filters.select_all';

        return {
            label,
            isVariant: false
        };
    }

}
